import * as React from "react";
import styled from "styled-components";
import { toast } from "react-toastify";

import { Box } from "src/library/components/Hedron";
import { Text } from "src/library/components/Text";
import {
  Select,
  Input
} from "src/library/components/Forms";
import { sentenceCase } from "src/library/utils/string-manipulations";
import { ICreditRequestReasonCode } from "src/modules/credit-request/api/credit-request.api";
import {
  CreditRequestAutoApprovalSetting,
  CreditRequestApprovalThreshold
} from "src/core/drops/api/drops.api";

interface ICreditRequestAutoApprovalThresholdProps {
  onChange: (data: CreditRequestAutoApprovalSetting) => void;
  data?: CreditRequestAutoApprovalSetting;
  overrideData?: CreditRequestAutoApprovalSetting;
  reasonData?: ICreditRequestReasonCode[];
}

enum CreditRequestAutoApprovalThresholdType {
  Flat = "flat",
  ReasonSpecific = "reason_specific",
}

type ICreditRequestReasonSpecificThresholdAttributes = { [P in string]: string | number };

/**
 * Credit request auto-approval thresholds
 */
export const CreditRequestAutoApprovalThresholds: React.ReactFCWithChildren<ICreditRequestAutoApprovalThresholdProps> = ({
  data,
  overrideData,
  reasonData,
  onChange
}) => {
  const [ thresholdType, setThresholdType ] = React.useState<CreditRequestAutoApprovalThresholdType>(!data || !data.threshold && !data.thresholds || !!data.threshold ? CreditRequestAutoApprovalThresholdType.Flat : CreditRequestAutoApprovalThresholdType.ReasonSpecific);
  const [ flatThreshold, setFlatThreshold ] = React.useState<string | number>(determineThresholdValue(data, overrideData));
  const [ reasonSpecificThresholds, setReasonSpecificThresholds ] = React.useState<ICreditRequestReasonSpecificThresholdAttributes>(determineThresholdsValue(reasonData, data, overrideData));

  const thresholdSelectOptions = React.useMemo(() => {
    const opts = [
      {
        value: CreditRequestAutoApprovalThresholdType.Flat,
        text: sentenceCase(CreditRequestAutoApprovalThresholdType.Flat).replace("_", " "),
        checked: thresholdType === CreditRequestAutoApprovalThresholdType.Flat
      }
    ];

    // If reasonData is given, add the reason specific option
    if (reasonData && reasonData.length > 0) {
      opts.push({
        value: CreditRequestAutoApprovalThresholdType.ReasonSpecific,
        text: sentenceCase(CreditRequestAutoApprovalThresholdType.ReasonSpecific).replace("_", " "),
        checked: thresholdType === CreditRequestAutoApprovalThresholdType.ReasonSpecific
      });
    }

    return opts;
  }, [ reasonData, thresholdType ]);

  /**
   * Validates percentage values
   */
  const validatePercentageInput = React.useCallback((input: string): string => {
    let parsedValue: number = typeof input === "string" ? parseInt(input) : input;

    // Return of the input is empty
    if (input.length === 0) {
      return input;
    }

    // Error, if the value is a number outside the percentage range 0 - 100
    if (isNaN(parsedValue) || !isNaN(parsedValue) && parsedValue < 0 || parsedValue > 100) {
      toast.error("Please provide a percentage value between 0 - 100");

      // If the parsedValue is a number, set the parsedValue to the closest
      // given value. Otherwise set the value to 0 regardless of the given value.
      if (!isNaN(parsedValue)) {
        if (parsedValue < 50) {
          parsedValue = 0;
        } else {
          parsedValue = 100;
        }
      } else {
        parsedValue = 0;
      }
    }

    return parsedValue.toString();
  }, []);

  /**
   * Handles the select onChange event
   */
  const handleSelection = React.useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const type = event.currentTarget.value as CreditRequestAutoApprovalThresholdType;
    let updatedData: CreditRequestAutoApprovalSetting = data || { enabled: true };

    // Update the thresholdType state
    setThresholdType(type);

    // Set the updated data on selection
    switch (type) {
      case CreditRequestAutoApprovalThresholdType.Flat: {
        const parsedValue: number = typeof flatThreshold === "string" ? parseInt(flatThreshold) : flatThreshold;

        updatedData = {
          ...updatedData,
          threshold: parsedValue
        };
        // Delete the thresholds attribute
        delete updatedData?.thresholds;
      }
        break;

      case CreditRequestAutoApprovalThresholdType.ReasonSpecific: {
        const reasonSpecificThresholds = determineThresholdsValue(reasonData, updatedData, overrideData);

        // Convert the reasonSpecificThresholds object to an array of reason objects
        const reasonThresholds: CreditRequestApprovalThreshold[] = Object.entries(reasonSpecificThresholds).map(entry => ({
          reasonCode: entry[ 0 ] as string,
          autoApprovalThreshold: entry[ 1 ] as number
        }));

        updatedData = {
          ...updatedData,
          thresholds: reasonThresholds
        };
        // Delete the threshold attribute
        delete updatedData?.threshold;
      }
        break;
    }

    // Call the external onChange functionality
    onChange(updatedData);
  }, [
    data,
    flatThreshold,
    onChange,
    overrideData,
    reasonData
  ]);

  /**
   * Handles the flat threshold change event
   */
  const handleFlatThresholdChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = validatePercentageInput(event.currentTarget.value);
    const parsedValue: number = value.length > 0 ? parseInt(value) : 0;

    // Update the flatThreshold state
    setFlatThreshold(value);

    // If data exists, call the onChange functionality with a valid value.
    if (data) {
      const updatedData: CreditRequestAutoApprovalSetting = {
        ...data,
        threshold: parsedValue
      };

      // Delete the thresholds attribute
      delete updatedData?.thresholds;
      // Call the external onChange functionality
      onChange(updatedData);
    }
  }, [
    data,
    onChange,
    validatePercentageInput
  ]);

  /**
   * Handles the reason specific threshold change event
   */
  const handleReasonSpecificThresholdChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const reasonCode = event.currentTarget.name;
    const value = validatePercentageInput(event.currentTarget.value);
    const parsedValue: number = value.length > 0 ? parseInt(value) : 0;

    // Update the reasonSpecificThresholds state
    setReasonSpecificThresholds(current => ({
      ...current,
      [ reasonCode ]: value
    }));

    // If data exists, call the onChange functionality with a valid value for
    // the reason of interest.
    if (data) {
      // Convert the reasonSpecificThresholds object to an array of reason objects
      const reasonThresholds: CreditRequestApprovalThreshold[] = Object.entries(reasonSpecificThresholds).map(entry => ({
        reasonCode: entry[ 0 ] as string,
        autoApprovalThreshold: entry[ 1 ] as number
      }));

      // Set the updated threshold entry data
      const reasonSpecificThresholdData: CreditRequestApprovalThreshold = {
        reasonCode,
        autoApprovalThreshold: parsedValue
      };

      let updatedData: CreditRequestAutoApprovalSetting = { ...data };
      let updatedThresholds: CreditRequestApprovalThreshold[] = [];

      // Attempt to find the matching reason of interest and update it.
      // Otherwise, simply set the thresholds with the new reason entry.
      if (reasonThresholds.length > 0) {
        const matchingReasonThreshold = reasonThresholds.find(reasonThreshold => reasonThreshold.reasonCode === reasonCode);

        if (matchingReasonThreshold) {
          // Set the reason of interest's new threshold value
          updatedThresholds = reasonThresholds.map(threshold => {
            // If the current threshold in the loop does not match the matching
            // reason of interest, simply return its value
            if (threshold.reasonCode !== matchingReasonThreshold.reasonCode) {
              return threshold;
            }

            return reasonSpecificThresholdData;
          });
        } else {
          // Push the new entry into the thresholds array
          updatedThresholds = [ ...reasonThresholds, reasonSpecificThresholdData ];
        }
      } else {
        updatedThresholds = [ reasonSpecificThresholdData ];
      }

      // Set the updated auto approval setting data with the updatedThresholds
      updatedData = {
        ...data,
        thresholds: updatedThresholds
      };
      // Delete the threshold attribute
      delete updatedData?.threshold;
      // Call the external onChange functionality
      onChange(updatedData);
    }
  }, [
    data,
    onChange,
    reasonSpecificThresholds,
    validatePercentageInput
  ]);

  // Return null if there is no threshold data given or the thresholds has not
  // been enabled
  if (!data || !data.enabled) {
    return null;
  }

  return (
    <StyledCreditRequestAutoApprovalThresholds>
      {thresholdSelectOptions.length > 1 && (
        <>
          <Text
            as="h3"
            fontSize="h4"
          >
            Threshold type
          </Text>

          <Select
            autoHideLabels
            as="select"
            className="select-box"
            value={thresholdType}
            name="credit-request-auto-approval-threshold-select"
            label="Threshold type"
            options={thresholdSelectOptions}
            handleChange={handleSelection}
          />
        </>
      )}

      <Box className="threshold-wrapper">
        {thresholdType === CreditRequestAutoApprovalThresholdType.Flat && (
          <>
            <Text
              as="h4"
              fontSize="h4"
            >
              Flat threshold (%)
            </Text>

            <Text as="p">
              A percentage of credit requests for an order regardless of the reason (0 = disabled).
            </Text>

            <Input
              autoHideLabels
              as="input"
              label="Flat threshold"
              name="flatThreshold"
              value={flatThreshold}
              handleChange={handleFlatThresholdChange}
            />
          </>
        )}

        {thresholdType === CreditRequestAutoApprovalThresholdType.ReasonSpecific && (
          <>
            {reasonData && reasonData.map(reason => (
              <React.Fragment key={reason.id}>
                <Text
                  as="h4"
                  fontSize="h4"
                >
                  {`${reason.name} threshold (%)`}
                </Text>

                <Text as="p">
                  {`${reason.description} (0 = disabled).`}
                </Text>

                <Input
                  autoHideLabels
                  as="input"
                  label={`${reason.name} threshold`}
                  name={reason.code}
                  value={reasonSpecificThresholds[ reason.code ] || 0}
                  handleChange={handleReasonSpecificThresholdChange}
                />
              </React.Fragment>
            ))}
          </>
        )}
      </Box>
    </StyledCreditRequestAutoApprovalThresholds>
  );
};

const StyledCreditRequestAutoApprovalThresholds = styled(Box)`
  h3,
  h4 {
    padding-top: 2.8rem;
  }
`;

/**
 * Determines the threshold value based on the given data
 */
const determineThresholdValue = (data?: CreditRequestAutoApprovalSetting, overrideData?: CreditRequestAutoApprovalSetting): number => {
  return data?.threshold || overrideData?.threshold || 0;
};

/**
 * Determines the threshold values based on the given data
 */
const determineThresholdsValue = (reasonData?: ICreditRequestReasonCode[], data?: CreditRequestAutoApprovalSetting, overrideData?: CreditRequestAutoApprovalSetting): ICreditRequestReasonSpecificThresholdAttributes => {
  // If there is no data provided, attempt to set default values for each of the
  // reason codes
  if (!data || !overrideData) {
    if (!reasonData || !reasonData.length) {
      return {};
    }

    const reasonsSpecificThresholds: ICreditRequestReasonSpecificThresholdAttributes[] = reasonData.map(reason => ({ [ reason.code ]: 0 }));

    return Object.assign({}, ...reasonsSpecificThresholds);
  }

  // If there is data provided
  if (data) {
    // If there is no reason data to verify the given data, use the given
    // data.thresholds instead
    if (!reasonData || !reasonData.length && !!data.thresholds?.length) {
      return Object.assign({}, ...data.thresholds as CreditRequestApprovalThreshold[]);
    }

    return reasonData.reduce((obj, reason) => {
      const matchingThreshold = data.thresholds?.find(threshold => threshold.reasonCode === reason.code);

      return {
        ...obj,
        [ reason.code ]: matchingThreshold?.autoApprovalThreshold || 0
      };
    }, {});
  }

  // If there is only overrideData, return it as an object
  if (overrideData && !!overrideData.thresholds?.length) {
    return Object.assign({}, ...overrideData.thresholds);
  }

  return {};
};
